<script setup lang="ts">
import type { BaseItem } from '~/models/Content/BaseItem'
import { computed, onUnmounted, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useContentHelper } from '~/composables/useContentHelper'
import useFilterStore from '~/stores/filter'
import useTime from '~/composables/useTime'
import ResourceFinderFilter from './ResourceFinderFilter.vue'
import CardSkeleton from '~/components/skeletons/CardSkeleton.vue'
import ResourceFinderFilterSkeleton from '~/components/skeletons/ResourceFinderFilterSkeleton.vue'
import ResourcesFilteredAwayCallout from '~/components/ResourceFinder/ResourcesFilteredAwayCallout.vue'
import ProductPackageIntro from '~/components/ResourceFinder/ProductPackageIntro.vue'
import CardRenderer from '~/components/cards/CardRenderer.vue'

const filterStore = useFilterStore()
const { filterWatchers } = filterStore
const { currentWeekNumber } = useTime()
const { isPackage, getParentLocationId } = useContentHelper()
const watchers = filterWatchers()
const {
  selectedHeaders,
  productHeaders,
  packageHeader,
  isLoading: storeIsLoading,
  resourceFilters,
  lastSelectedHeader,
  viewMode,
  fileView,
} = storeToRefs(filterStore)

// because storeIsLoading is toggled a few times before it really settles on a value, we have to do this:
const isLoading = ref(storeIsLoading.value)
let loadingTimeout: ReturnType<typeof setTimeout>
watch(storeIsLoading, () => {
  clearTimeout(loadingTimeout)
  loadingTimeout = setTimeout(() => {
    isLoading.value = storeIsLoading.value
  }, 100)
})

const props = withDefaults(defineProps<{
  showFilter?: boolean
  showPackageIntro?: boolean
  isAnchor?: boolean
  enableGradeFilter?: boolean
  enableSubjectFilter?: boolean
}>(), {
  showFilter: true,
  showPackageIntro: false,
  isAnchor: false,
  enableGradeFilter: true,
  enableSubjectFilter: true,
})

const currentWeek = ref(currentWeekNumber)

const sortResources = (a: BaseItem, b: BaseItem) =>
  (getParentLocationId(a) - getParentLocationId(b))

const resources = computed<BaseItem[]>(() => {
  if (selectedHeaders.value.length === 0) return [
    ...productHeaders.value.learningPath,
    ...(packageHeader.value ? packageHeader.value.children : []),
  ]
  return selectedHeaders.value[selectedHeaders.value.length - 1].children
})

const filteredResources = computed<BaseItem[]>(() => [...resources.value].sort(sortResources)
  .filter((resource) =>
   // gradeFilterFunc is the only filter function that is really used here, the other one is taskTypeFilterFunc, and we have nowhere for users to filter by task type
    Object.values(resourceFilters.value).filter(() => props.enableGradeFilter)
    .every((filter: CallableFunction) => !props.showFilter || filter(resource))
  ))

const filteredProductPackages = computed(() => filteredResources.value
  .filter((resource) => isPackage(resource)))

const autoSelectFilter = computed(() => props.showFilter
  && filteredResources.value.length === 1
  && filteredProductPackages.value.length === 1)

const showIntro = computed(() => props.showPackageIntro
  && lastSelectedHeader.value
  && lastSelectedHeader.value?.intro.length > 2)

const hasResources = computed(() => filteredResources.value?.length)
onUnmounted(() => watchers.stopFilterWatchers())
</script>
<template>
  <ResourceFinderFilterSkeleton v-if="isLoading" />
  <ResourceFinderFilter
    v-if="showFilter"
    v-show="!isLoading"
    :auto-select="autoSelectFilter"
    :enable-grade-filter="enableGradeFilter"
    :enable-subject-filter="enableSubjectFilter"
  />
  <section class="relative m-auto mb-12 max-w-screen-au px-4 sm:px-8">
    <ul
      v-if="isLoading"
      class="mb-12 grid grid-cols-1 gap-6 xs:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5"
    >
      <li
        v-for="i in 8"
        :key="i"
      >
        <CardSkeleton />
      </li>
    </ul>
    <template v-else>
      <ProductPackageIntro
        v-if="showIntro"
        :package="lastSelectedHeader"
      />
      <ResourcesFilteredAwayCallout
        v-if="!hasResources && resources.length > 0 && !storeIsLoading"
        :resources="resources"
      />
      <ul
        class="mt-8 grid grid-cols-1"
        :class="{
          'gap-3 sm:grid-cols-2': fileView,
          'gap-6 xs:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5': !fileView,
        }"
      >
        <li
          v-for="resource of filteredResources"
          :key="`resource-${resource.locationId}week-${currentWeek}`"
          :class="{ 'border-b border-gray-10 pb-3': fileView }"
        >
          <CardRenderer
            :resource="resource"
            :view-mode="viewMode"
            :is-anchor="props.isAnchor"
          />
        </li>
      </ul>
    </template>
  </section>
</template>
